/**
 * Vaperite store master — every Vaperite branch.
 *
 * Sources:
 *  - Branch list, storefront + Uber Eats links: "Vaperite Store Master.xlsx".
 *  - Address, trading hours, email and phone for the 10 live branches:
 *    "Store_Information_1784124840 - Deeliver.xlsx" (Vaperite, 31 Jul 2026).
 *  - Coordinates: the Google Places entry for the branch itself, so a pin lands
 *    on the shop rather than on the centre of the suburb.
 *
 * Deeliver's own Diep River location is deliberately excluded: it is not a
 * Vaperite branch.
 *
 * `isActive` marks the 10 branches trading with Deeliver. The staging `stores`
 * table carries the same flag; this file is the reference copy that also holds
 * the address, hours and links the table has no columns for.
 */

export interface VaperiteTradingSlot {
  /** Day range label, e.g. "Mon – Fri". */
  days: string;
  /** 24h "HH:MM". */
  open: string;
  /** 24h "HH:MM". */
  close: string;
}

export interface VaperiteStore {
  id: string;
  branch: string;
  address: string;
  city: string;
  province: string;
  zip: string;
  country: string;
  phone: string;
  email: string;
  /** True for the branches trading with Deeliver. */
  isActive: boolean;
  lat: number;
  lng: number;
  /** Hours as published by Vaperite. Null where we do not have them. */
  tradingHours: VaperiteTradingSlot[] | null;
  /** Vaperite storefront page. */
  link: string | null;
  /** Uber Eats storefront, where this branch is listed. */
  uberEatsLink: string | null;
}

export const VAPERITE_STORES: VaperiteStore[] = [
  {
    id: "30704",
    branch: "Vaperite Port Elizabeth",
    address: "9th Ave Shopping Centre, Corner Of Main Road & 9th Avenue, Walmer",
    city: "Port Elizabeth",
    province: "Eastern Cape",
    zip: "6070",
    country: "South Africa",
    phone: "0100232421",
    email: "portelizabeth@vaperite.co.za",
    isActive: false,
    lat: -33.978802,
    lng: 25.583521,
    tradingHours: null,
    link: "https://vaperite.co.za/stores/vaperite-port-elizabeth",
    uberEatsLink: "https://www.ubereats.com/za/store/vaperite-walmer/dRwPHTJ6WrGrFCGgue87pw",
  },
  {
    id: "30710",
    branch: "Vaperite East London",
    address: "Patcyn Shopping Centre, 52 Vincent, Frere Rd, Vincent",
    city: "East London",
    province: "Eastern Cape",
    zip: "5217",
    country: "South Africa",
    phone: "0100232422",
    email: "eastlondon@vaperite.co.za",
    isActive: false,
    lat: -32.982928,
    lng: 27.901896,
    tradingHours: null,
    link: "https://vaperite.co.za/stores/vaperite-patcyn-shopping-centre-east-london/",
    uberEatsLink: "https://www.ubereats.com/za/store/vaperite-patcyn/wjObh3tAXMeXAyIU2FUQ7g",
  },
  {
    id: "630631",
    branch: "Vaperite Fleurdal Bloemfontein",
    address: "Shop 7, Fleurdal Centre 123 Curie Avenue, Fleurdal",
    city: "Bloemfontein",
    province: "Free State",
    zip: "9317",
    country: "South Africa",
    phone: "0100237459",
    email: "fleurdal@vaperite.co.za",
    isActive: false,
    lat: -29.154223,
    lng: 26.189208,
    tradingHours: null,
    link: "https://vaperite.co.za/stores/vaperite-fleurdal-bloemfontein/",
    uberEatsLink: null,
  },
  {
    id: "594770",
    branch: "Vaperite Mimosa Mall",
    address: "Mimosa Mall Food Court, 131 Kellner St, Brandwag",
    city: "Bloemfontein",
    province: "Free State",
    zip: "9301",
    country: "South Africa",
    phone: "0100232403",
    email: "mimosa@vaperite.co.za",
    isActive: false,
    lat: -29.10867,
    lng: 26.20129,
    tradingHours: null,
    link: "https://vaperite.co.za/stores/vaperite-mimosa-mall/",
    uberEatsLink: null,
  },
  {
    id: "615005",
    branch: "Vaperite Riverstone Mall",
    address: "Corner Pierneef Blvd and Jim Fouche Rd, Meyerton",
    city: "Meyerton",
    province: "Gauteng",
    zip: "1961",
    country: "South Africa",
    phone: "0100237453",
    email: "riverstone@vaperite.co.za",
    isActive: false,
    lat: -26.579879,
    lng: 28.017632,
    tradingHours: null,
    link: "https://vaperite.co.za/stores/vaperite-riverstone-mall/",
    uberEatsLink: null,
  },
  {
    id: "30696",
    branch: "Vaperite Valley View Retail Centre",
    address: "Valley View Retail Centre, Cnr Van Oordt St, Robert Broom Dr, Noordheuwel, Krugersdorp",
    city: "Krugersdorp",
    province: "Gauteng",
    zip: "1734",
    country: "South Africa",
    phone: "0100232410",
    email: "valleyview@vaperite.co.za",
    isActive: true,
    lat: -26.082094,
    lng: 27.804807,
    tradingHours: [
      { days: "Mon – Fri", open: "09:00", close: "18:00" },
      { days: "Sat", open: "09:00", close: "16:00" },
      { days: "Sun", open: "09:00", close: "15:00" },
    ],
    link: "https://vaperite.co.za/stores/vaperite-valley-view/",
    uberEatsLink: "https://www.ubereats.com/za/store/vaperite-valley-view/kUoyz1OJVAyLUpm5iks_4w",
  },
  {
    id: "513801",
    branch: "Vaperite Clearwater Mall",
    address: "Clearwater Mall, Hendrik Potgieter Rd, Christiaan de Wet Rd, Strubens Valley",
    city: "Roodepoort",
    province: "Gauteng",
    zip: "1735",
    country: "South Africa",
    phone: "0100237448",
    email: "clearwater@vaperite.co.za",
    isActive: false,
    lat: -26.12749,
    lng: 27.904667,
    tradingHours: null,
    link: "https://vaperite.co.za/stores/vaperite-clearwater-mall/",
    uberEatsLink: "https://www.ubereats.com/za/store/vaperite-clearwater-mall/Qyg3Lh1fU22AfE1UPui7Fg",
  },
  {
    id: "639812",
    branch: "Vaperite Bracken Villa Alberton",
    address: "Bracken Villa Shopping Centre, 23 Abel Moller St, Brackenhurst, Alberton",
    city: "Alberton",
    province: "Gauteng",
    zip: "1449",
    country: "South Africa",
    phone: "0100237461",
    email: "brackenvilla@vaperite.co.za",
    isActive: false,
    lat: -26.30978,
    lng: 28.10655,
    tradingHours: null,
    link: "https://vaperite.co.za/stores/vaperite-bracken-villa-alberton/",
    uberEatsLink: null,
  },
  {
    id: "514637",
    branch: "Vaperite Cresta",
    address: "Cresta Shopping Centre, Republic Rd, Blackheath, Randburg",
    city: "Randburg",
    province: "Gauteng",
    zip: "2196",
    country: "South Africa",
    phone: "0100232449",
    email: "cresta@vaperite.co.za",
    isActive: false,
    lat: -26.131032,
    lng: 27.97497,
    tradingHours: null,
    link: "https://vaperite.co.za/stores/vaperite-cresta/",
    uberEatsLink: "https://www.ubereats.com/za/store/vaperite-cresta/QWO4pYwhXsiLOFQj6Ax1Rg",
  },
  {
    id: "30664",
    branch: "Vaperite Appletons Village Shopping Centre",
    address: "Malibongwe Drive, Randburg",
    city: "Randburg",
    province: "Gauteng",
    zip: "2194",
    country: "South Africa",
    phone: "0100232406",
    email: "appletonsvillage@vaperite.co.za",
    isActive: true,
    lat: -26.094202,
    lng: 27.985041,
    tradingHours: [
      { days: "Mon – Fri", open: "09:00", close: "18:00" },
      { days: "Sat", open: "09:00", close: "17:00" },
      { days: "Sun", open: "09:00", close: "15:00" },
    ],
    link: "https://vaperite.co.za/stores/vaperite-appletons-village-shopping-centre-randburg/",
    uberEatsLink: "https://www.ubereats.com/za/store/vaperite-appletons/tkEB7WcHUv-z9zvOv7Hmbw",
  },
  {
    id: "504031",
    branch: "Vaperite SUPERSPAR Bel Air",
    address: "Bel Air Shopping Centre, 50 Malibongwe Drove, Northriding, Randburg",
    city: "Randburg",
    province: "Gauteng",
    zip: "2169",
    country: "South Africa",
    phone: "0100232407",
    email: "bellairs@vaperite.co.za",
    isActive: false,
    lat: -26.060279,
    lng: 27.957776,
    tradingHours: null,
    link: "https://vaperite.co.za/stores/vaperite-superspar-bel-air/",
    uberEatsLink: "https://www.ubereats.com/za/store/vaperite-superspar-bel-air/kLiYI70aV0S7BvUfhMV94A",
  },
  {
    id: "445670",
    branch: "Vaperite Rosebank @ The Zone",
    address: "The Zone Shopping Centre, 177 Oxford Rd, Rosebank",
    city: "Johannesburg",
    province: "Gauteng",
    zip: "2196",
    country: "South Africa",
    phone: "0100232416",
    email: "rosebank@vaperite.co.za",
    isActive: false,
    lat: -26.145128,
    lng: 28.043665,
    tradingHours: null,
    link: "https://vaperite.co.za/stores/vaperite-rosebank-the-zone/",
    uberEatsLink: "https://www.ubereats.com/za/store/vaperite-the-zone/gxRRN9oNVwejirRc1Cai3A",
  },
  {
    id: "621541",
    branch: "Vaperite SUPERSPAR Olivedale",
    address: "All Saints Shopping Centre, Windsor Way, Olivedale",
    city: "Randburg",
    province: "Gauteng",
    zip: "2158",
    country: "South Africa",
    phone: "0100237456",
    email: "allsaints@vaperite.co.za",
    isActive: false,
    lat: -26.053343,
    lng: 27.971446,
    tradingHours: null,
    link: "https://vaperite.co.za/stores/vaperite-superspar-olivedale/",
    uberEatsLink: null,
  },
  {
    id: "468573",
    branch: "Vaperite Norwood",
    address: "Shop No 7, 66 Grant Avenue Corner Nellie Road",
    city: "Norwood",
    province: "Gauteng",
    zip: "2192",
    country: "South Africa",
    phone: "0100232426",
    email: "norwood@vaperite.co.za",
    isActive: false,
    lat: -26.15836,
    lng: 28.07653,
    tradingHours: null,
    link: "https://vaperite.co.za/stores/vaperite-norwood",
    uberEatsLink: null,
  },
  {
    id: "30652",
    branch: "Vaperite Blubird Shopping Centre",
    address: "18 Main Rd, BluBird Shopping Centre, Birnam",
    city: "Johannesburg",
    province: "Gauteng",
    zip: "2196",
    country: "South Africa",
    phone: "0100232404",
    email: "blubird@vaperite.co.za",
    isActive: false,
    lat: -26.129429,
    lng: 28.065577,
    tradingHours: null,
    link: "https://vaperite.co.za/stores/vaperite-birnam-blubird-shopping-centre/",
    uberEatsLink: "https://www.ubereats.com/za/store/vaperite-blubird/riOqQWuDV6mAQfNM4VWjsQ",
  },
  {
    id: "445679",
    branch: "Vaperite Sandton City",
    address: "Sandton City, 83 Rivonia Rd, Sandhurst, Sandton",
    city: "Sandton",
    province: "Gauteng",
    zip: "2196",
    country: "South Africa",
    phone: "0100232427",
    email: "sandton@vaperite.co.za",
    isActive: false,
    lat: -26.108907,
    lng: 28.050222,
    tradingHours: null,
    link: "https://vaperite.co.za/stores/vaperite-sandton/",
    uberEatsLink: "https://www.ubereats.com/za/store/vaperite-sandton-city/UlkRaYLXW3q3ps95Ogzseg",
  },
  {
    id: "30679",
    branch: "Vaperite The Buzz",
    address: "Shop 4a, The Buzz Shopping Centre, Fourways, Sandton",
    city: "Sandton",
    province: "Gauteng",
    zip: "2021",
    country: "South Africa",
    phone: "0100232408",
    email: "thebuzz@vaperite.co.za",
    isActive: false,
    lat: -26.023434,
    lng: 27.99048,
    tradingHours: null,
    link: "https://vaperite.co.za/stores/vaperite-the-buzz-shopping-centre/",
    uberEatsLink: "https://www.ubereats.com/za/store/vaperite-the-buzz/mNrxjtxXWtK56spnVq_n4w",
  },
  {
    id: "30613",
    branch: "Vaperite Bedford Village",
    address: "Corner Nicol And Van Buuren Road, Bedford Village Shopping Centre, Shop 1, Bedfordview, Germiston",
    city: "Germiston",
    province: "Gauteng",
    zip: "2007",
    country: "South Africa",
    phone: "0100232401",
    email: "bedfordvillage@vaperite.co.za",
    isActive: true,
    lat: -26.180511,
    lng: 28.135896,
    tradingHours: [
      { days: "Mon – Sat", open: "09:00", close: "18:00" },
      { days: "Sun", open: "10:00", close: "17:00" },
    ],
    link: "https://vaperite.co.za/stores/vaperite-bedfordview/",
    uberEatsLink: "https://www.ubereats.com/za/store/vaperite-bedford-village/pA6-Hfx3Xtab_VtrsyrEdg",
  },
  {
    id: "30669",
    branch: "Vaperite Grayston",
    address: "Grayston Drive And Helen Road, Grayston Shopping Centre, Sandton",
    city: "Sandton",
    province: "Gauteng",
    zip: "2196",
    country: "South Africa",
    phone: "0100232405",
    email: "grayston@vaperite.co.za",
    isActive: true,
    lat: -26.10039,
    lng: 28.065831,
    tradingHours: [
      { days: "Mon – Fri", open: "08:00", close: "18:00" },
      { days: "Sat", open: "09:00", close: "15:00" },
      { days: "Sun", open: "09:00", close: "15:00" },
    ],
    link: "https://vaperite.co.za/stores/vaperite-grayston-shopping-centre/",
    uberEatsLink: "https://www.ubereats.com/za/store/vaperite-grayston/yk8oqgGzXKiovff7fHu5gg",
  },
  {
    id: "30683",
    branch: "Vaperite Broadacres",
    address: "Corner Cedar Avenue and Valley Rd, Fourways, 2194",
    city: "Sandton",
    province: "Gauteng",
    zip: "2194",
    country: "South Africa",
    phone: "0100232409",
    email: "broadacres@vaperite.co.za",
    isActive: false,
    lat: -26.000328,
    lng: 27.983084,
    tradingHours: null,
    link: "https://vaperite.co.za/stores/vaperite-broadacres-shopping-centre/",
    uberEatsLink: null,
  },
  {
    id: "450570",
    branch: "Vaperite SilverCrest",
    address: "Shop 5, SilverCrest Shopping Centre, 16 Boundary Rd, Silvamonte",
    city: "Johannesburg",
    province: "Gauteng",
    zip: "2192",
    country: "South Africa",
    phone: "0100232418",
    email: "silvercrest@vaperite.co.za",
    isActive: false,
    lat: -26.13674,
    lng: 28.11591,
    tradingHours: null,
    link: "https://vaperite.co.za/stores/vaperite-silvercrest-shopping-centre/",
    uberEatsLink: null,
  },
  {
    id: "513802",
    branch: "Vaperite Fourways Mall",
    address: "Shop NG53, Game Court, Fourways Mall, 11 Ruby Cl, Witkoppen, Sandton",
    city: "Sandton",
    province: "Gauteng",
    zip: "2068",
    country: "South Africa",
    phone: "0100237447",
    email: "fourwaysmall@vaperite.co.za",
    isActive: false,
    lat: -26.017837,
    lng: 28.005176,
    tradingHours: null,
    link: "https://vaperite.co.za/stores/vaperite-fourways-mall/",
    uberEatsLink: null,
  },
  {
    id: "615001",
    branch: "Vaperite The Verge Shopping Centre",
    address: "100 Linksfield Road, Dowerglen, Edenvale",
    city: "Edenvale",
    province: "Gauteng",
    zip: "1609",
    country: "South Africa",
    phone: "0100237454",
    email: "therverge@vaperite.co.za",
    isActive: false,
    lat: -26.149261,
    lng: 28.135652,
    tradingHours: null,
    link: "https://vaperite.co.za/stores/the-verge-shopping-centre/",
    uberEatsLink: null,
  },
  {
    id: "508994",
    branch: "Vaperite Greenstone Centre",
    address: "Entrance 4, Shop U005, Greenstone Shopping Centre, Corner Modderfontein road and Van Riebeeck Avenue, Greenstone Hill",
    city: "Johannesburg",
    province: "Gauteng",
    zip: "1609",
    country: "South Africa",
    phone: "0100232400",
    email: "greenstone@vaperite.co.za",
    isActive: false,
    lat: -26.117417,
    lng: 28.141832,
    tradingHours: null,
    link: "https://vaperite.co.za/stores/vaperite-greenstone-centre/",
    uberEatsLink: null,
  },
  {
    id: "621545",
    branch: "Vaperite Head Office",
    address: "Linbro Business Park, 8 Gemini St, Frankenwald",
    city: "Johannesburg",
    province: "Gauteng",
    zip: "2090",
    country: "South Africa",
    phone: "0100232400",
    email: "info@vaperite.co.za",
    isActive: false,
    lat: -26.070067,
    lng: 28.114777,
    tradingHours: null,
    link: "https://vaperite.co.za/stores/vaperite-head-office/",
    uberEatsLink: "https://www.ubereats.com/za/store/vaperite-head-offices/IIU9Zwn8XcuzxXz_Zp_XWg",
  },
  {
    id: "445677",
    branch: "Vaperite Mall of Africa",
    address: "Shop 1097, Magwa Cres, Waterval City, Midrand",
    city: "Midrand",
    province: "Gauteng",
    zip: "1686",
    country: "South Africa",
    phone: "0100232417",
    email: "MallOfAfrica@vaperite.co.za",
    isActive: true,
    lat: -26.014223,
    lng: 28.105942,
    tradingHours: [
      { days: "Mon – Sun", open: "09:00", close: "20:00" },
    ],
    link: "https://vaperite.co.za/stores/vaperite-mall-of-africa/",
    uberEatsLink: null,
  },
  {
    id: "30694",
    branch: "Vaperite Northmead Shopping Centre",
    address: "Northmead Shopping Centre, 19 1st Street, Northmead, Benoni",
    city: "Benoni",
    province: "Gauteng",
    zip: "1501",
    country: "South Africa",
    phone: "0100232412",
    email: "northmead@vaperite.co.za",
    isActive: true,
    lat: -26.170674,
    lng: 28.329331,
    tradingHours: [
      { days: "Mon – Sat", open: "09:00", close: "18:00" },
      { days: "Sun", open: "09:00", close: "16:00" },
    ],
    link: "https://vaperite.co.za/stores/vaperite-northmead-shopping-centre-benoni/",
    uberEatsLink: null,
  },
  {
    id: "474165",
    branch: "Vaperite Glen Acres",
    address: "Glen Acres Shopping Centre Corner Dann And Monument Rd, Glen Marais, Kempton Park",
    city: "Kempton Park",
    province: "Gauteng",
    zip: "1619",
    country: "South Africa",
    phone: "0100232415",
    email: "glenacres@vaperite.co.za",
    isActive: true,
    lat: -26.07841,
    lng: 28.248603,
    tradingHours: [
      { days: "Mon – Fri", open: "09:00", close: "18:00" },
      { days: "Sat", open: "09:00", close: "17:00" },
      { days: "Sun", open: "09:00", close: "16:00" },
    ],
    link: "https://vaperite.co.za/stores/glen-acres-shopping-centre/",
    uberEatsLink: "https://www.ubereats.com/za/store/vaperite-glen-acres/yrD6YgWzXS-mhDTI5gw4_g",
  },
  {
    id: "30698",
    branch: "Vaperite Parkview Shopping Centre",
    address: "Woolworths Entrance, Parkview Shopping Centre, Garsfontein Road And Netcare Street, Moreleta Park",
    city: "Pretoria",
    province: "Gauteng",
    zip: "0044",
    country: "South Africa",
    phone: "0100232413",
    email: "parkview@vaperite.co.za",
    isActive: true,
    lat: -25.818681,
    lng: 28.303347,
    tradingHours: [
      { days: "Mon – Fri", open: "09:00", close: "18:00" },
      { days: "Sat", open: "09:00", close: "16:00" },
      { days: "Sun", open: "09:00", close: "15:00" },
    ],
    link: "https://vaperite.co.za/stores/vaperite-parkview-shopping-centre-pretoria/",
    uberEatsLink: "https://www.ubereats.com/za/store/vaperite-parkview/Ng67LbIhXDSD6GYHKBh2Yg",
  },
  {
    id: "30701",
    branch: "Vaperite Lynnwood",
    address: "Shop 105, Lynnwood Lane Retail Centre, Corner Lynnwood Lane &, Equestria Rd, Equestria",
    city: "Pretoria",
    province: "Gauteng",
    zip: "1084",
    country: "South Africa",
    phone: "0100232414",
    email: "lynnwood@vaperite.co.za",
    isActive: false,
    lat: -25.768232,
    lng: 28.328489,
    tradingHours: null,
    link: "https://vaperite.co.za/stores/vaperite-lynnwood-lane-retail-centre-pretoria/",
    uberEatsLink: null,
  },
  {
    id: "515201",
    branch: "Vaperite Kolonnade",
    address: "Entrance 1, Kolonnade Shopping Centre, Sefako Makgatho Dr, Montana Park",
    city: "Pretoria",
    province: "Gauteng",
    zip: "0159",
    country: "South Africa",
    phone: "0100232450",
    email: "kolonnade@vaperite.co.za",
    isActive: false,
    lat: -25.680738,
    lng: 28.249533,
    tradingHours: null,
    link: "https://vaperite.co.za/stores/vaperite-kolonnade/",
    uberEatsLink: "https://www.ubereats.com/za/store/vaperite-kolonnade/NHav2-ZZW3eP51pdbnzgNg",
  },
  {
    id: "630636",
    branch: "Vaperite Cotswold Square",
    address: "Shop 1113, Cotswold Square, Inanda Road, Pinetown",
    city: "Hillcrest",
    province: "KwaZulu-Natal",
    zip: "3650",
    country: "South Africa",
    phone: "0100237460",
    email: "cotswold@vaperite.co.za",
    isActive: false,
    lat: -29.758589,
    lng: 30.795304,
    tradingHours: null,
    link: "https://vaperite.co.za/stores/vaperite-cotswold-square/",
    uberEatsLink: null,
  },
  {
    id: "622709",
    branch: "Vaperite SUPERSPAR Chatsworth",
    address: "17 Joyhurst Street, Westcliff",
    city: "Chatsworth",
    province: "KwaZulu-Natal",
    zip: "4092",
    country: "South Africa",
    phone: "0100237457",
    email: "chatsworth@vaperite.co.za",
    isActive: false,
    lat: -29.912182,
    lng: 30.882516,
    tradingHours: null,
    link: "https://vaperite.co.za/stores/vaperite-superspar-chatsworth/",
    uberEatsLink: null,
  },
  {
    id: "597459",
    branch: "Vaperite Cornubia Mall",
    address: "Cornubia Mall Corner of Flanders Drive & Tacoma Drive, Blackburn Estate,",
    city: "Mount Edgecombe",
    province: "KwaZulu-Natal",
    zip: "4319",
    country: "South Africa",
    phone: "0100237451",
    email: "cornubia@vaperite.co.za",
    isActive: false,
    lat: -29.709448,
    lng: 31.056378,
    tradingHours: null,
    link: "https://vaperite.co.za/stores/vaperite-cornubia-mall/",
    uberEatsLink: null,
  },
  {
    id: "622740",
    branch: "Vaperite SUPERSPAR Waterloo",
    address: "Waterloo Shopping Centre, 161 Crocodile Road",
    city: "Verulam",
    province: "KwaZulu-Natal",
    zip: "4340",
    country: "South Africa",
    phone: "0100237458",
    email: "waterloo@vaperite.co.za",
    isActive: false,
    lat: -29.670367,
    lng: 31.054714,
    tradingHours: null,
    link: "https://vaperite.co.za/stores/vaperite-superspar-waterloo/",
    uberEatsLink: null,
  },
  {
    id: "445675",
    branch: "Vaperite Gateway",
    address: "Gateway Theatre Of Shopping, 1 Palm Blvd, Umhlanga Ridge",
    city: "Umhlanga",
    province: "KwaZulu-Natal",
    zip: "4320",
    country: "South Africa",
    phone: "0100237445",
    email: "Gateway@vaperite.co.za",
    isActive: false,
    lat: -29.725912,
    lng: 31.065399,
    tradingHours: null,
    link: "https://vaperite.co.za/stores/vaperite-gateway/",
    uberEatsLink: "https://www.ubereats.com/za/store/vaperite-gateway/O7GSsb9LWJumEjwhRRHWyA",
  },
  {
    id: "506471",
    branch: "Vaperite Oceans Mall",
    address: "Oceans Mall, Lagoon Dr, Umhlanga",
    city: "Umhlanga",
    province: "KwaZulu-Natal",
    zip: "4320",
    country: "South Africa",
    phone: "0100237445",
    email: "oceansmall@vaperite.co.za",
    isActive: false,
    lat: -29.724891,
    lng: 31.085601,
    tradingHours: null,
    link: "https://vaperite.co.za/stores/vaperite-oceans-mall/",
    uberEatsLink: null,
  },
  {
    id: "617054",
    branch: "Vaperite President Boulevard",
    address: "R24, Waterval East, Rustenburg, 2999",
    city: "Rustenburg",
    province: "North West",
    zip: "2999",
    country: "South Africa",
    phone: "0100237455",
    email: "president@vaperite.co.za",
    isActive: false,
    lat: -25.704566,
    lng: 27.259111,
    tradingHours: null,
    link: "https://vaperite.co.za/stores/vaperite-president-boulevard/",
    uberEatsLink: null,
  },
  {
    id: "448130",
    branch: "Vaperite Stellenbosch",
    address: "38 Bird St, Stellenbosch Central, Stellenbosch",
    city: "Stellenbosch",
    province: "Western Cape",
    zip: "7600",
    country: "South Africa",
    phone: "0100232423",
    email: "stellenbosch@vaperite.co.za",
    isActive: false,
    lat: -33.936724,
    lng: 18.859633,
    tradingHours: null,
    link: "https://vaperite.co.za/stores/vaperite-stellenbosch/",
    uberEatsLink: "https://www.ubereats.com/za/store/vaperite-stellenbosch/lzOAL9mMVbGos34YNPjtDQ",
  },
  {
    id: "",
    branch: "Vaperite Canal Walk",
    address: "Canal Walk Shopping Centre, 490 Century Blvd, Century City",
    city: "Cape Town",
    province: "Western Cape",
    zip: "7446",
    country: "South Africa",
    phone: "0100232424",
    email: "canalwalk@vaperite.co.za",
    isActive: true,
    lat: -33.892278,
    lng: 18.511661,
    tradingHours: [
      { days: "Mon – Sun", open: "09:00", close: "21:00" },
    ],
    link: "https://vaperite.co.za/stores/vaperite-canal-walk/",
    uberEatsLink: "https://www.ubereats.com/za/store/vaperite-canal-walk/1PypJzaOXg-w1N02lBcceA",
  },
  {
    id: "",
    branch: "Vaperite West Coast",
    address: "Shop 27A West Coast Village Shopping Centre Cnr West Coast Road And, Sandown Rd E, Bloubergstrand",
    city: "Cape Town",
    province: "Western Cape",
    zip: "7741",
    country: "South Africa",
    phone: "0100232420",
    email: "westcoastvillage@vaperite.co.za",
    isActive: false,
    lat: -33.804719,
    lng: 18.484929,
    tradingHours: null,
    link: "https://vaperite.co.za/stores/vaperite-west-coast-village/",
    uberEatsLink: "https://www.ubereats.com/za/store/vaperite-west-coast/zWiWe5aDV5a8QEmz7J8-Qg",
  },
  {
    id: "",
    branch: "Vaperite Cape Gate Mall",
    address: "Entrance 4, Cnr Okavango and, The Cape Gate Shopping Centre, De Bron Rd, Brackenfell",
    city: "Cape Town",
    province: "Western Cape",
    zip: "7562",
    country: "South Africa",
    phone: "0100237452",
    email: "capegate@vaperite.co.za",
    isActive: true,
    lat: -33.851079,
    lng: 18.697183,
    tradingHours: [
      { days: "Mon – Sat", open: "09:00", close: "20:00" },
      { days: "Sun", open: "09:00", close: "17:00" },
    ],
    link: "https://vaperite.co.za/stores/vaperite-cape-gate/",
    uberEatsLink: "https://www.ubereats.com/za/store/vaperite-capegate/pFRIP8xsV3aZvOWck9mdxA/a454483f-cc6c-5776-99bc-e59c93d99dc4",
  },
  {
    id: "",
    branch: "Vaperite Richmond Corner",
    address: "Richmond Corner Shopping Centre Corner Of Plattekloof Road And, Tygerberg Valley Rd, Richwood",
    city: "Cape Town",
    province: "Western Cape",
    zip: "7441",
    country: "South Africa",
    phone: "0100232419",
    email: "richmond@vaperite.co.za",
    isActive: false,
    lat: -33.848244,
    lng: 18.538807,
    tradingHours: null,
    link: "https://vaperite.co.za/stores/vaperite-richmond-corner/",
    uberEatsLink: "https://www.ubereats.com/za/store/vaperite-richmond-corner/8uX9JfeAUs-gq-l3ZdohsA",
  },
  {
    id: "30691",
    branch: "Vaperite Boksburg K90 Shopping Centre",
    address: "K90 Centre, North Rand Rd & Rondebult Rd, Jansen Park",
    city: "Boksburg",
    province: "Gauteng",
    zip: "1459",
    country: "South Africa",
    phone: "0100232411",
    email: "k90@vaperite.co.za",
    isActive: false,
    lat: -26.179617,
    lng: 28.245434,
    tradingHours: [
      { days: "Mon – Fri", open: "09:00", close: "18:00" },
      { days: "Sat", open: "08:00", close: "16:00" },
      { days: "Sun", open: "09:00", close: "14:00" },
    ],
    link: "https://vaperite.co.za/stores/vaperite-boksburg-k90-shopping-centre/",
    uberEatsLink: "https://www.ubereats.com/za/store/vaperite-k90/jjU6T_6JWYKHpkclQkv6Bw",
  },
];

/** The branches trading with Deeliver. */
export const ACTIVE_VAPERITE_STORES: VaperiteStore[] = VAPERITE_STORES.filter((s) => s.isActive);

/** "Mon – Fri 09:00–18:00 · Sat 09:00–17:00" */
export const formatTradingHours = (slots: VaperiteTradingSlot[] | null): string =>
  slots?.map((s) => `${s.days} ${s.open}–${s.close}`).join(' · ') ?? '—';
